import { computed } from 'vue'

import { primaryNavigation } from '@/shared/config/navigation'
import type { NavigationItem } from '@/shared/types/navigation'
import { useVpnStore } from '@/modules/vpn/store/vpnStore'
import { usePoolStore } from '@/modules/vpn/store/poolStore'

/**
 * IGRA-110 R21 — участник общей подписки подписку не покупал: «Подписка» для
 * него — раздел про то, чего у него нет. «Поделиться подпиской» — тоже:
 * приглашать может только владелец (R1), и вкладка обещала бы участнику
 * действие, которого у него не будет.
 *
 * «Друзья» участнику открыты с 2026-09-25 (HK-IGRA.734): у него своя
 * реферальная ссылка, а дни за его друзей идут в семейную подписку.
 *
 * Молчащий ответ (овервью ещё не прочитан) не прячет ничего: спрятать вкладку
 * у обычного человека хуже, чем показать её участнику на долю секунды.
 */
const HIDDEN_FOR_POOL_MEMBER = ['subscription', 'pool']

/**
 * Решение оператора 2026-09-29 — пока подписки не было ни разу (`access`
 * null в ПРОЧИТАННОМ овервью), «Маршрутизация», «Награды» и «Устройства» в
 * меню не нужны: настраивать, считать и подключать ещё нечего. Молчащий ответ
 * не прячет ничего — то же правило, что у остальных фильтров ниже.
 */
const HIDDEN_BEFORE_PURCHASE = ['routing', 'achievements', 'devices']

/**
 * Пункты меню, которые можно показывать этому человеку. Одно правило на верхнюю
 * полоску, нижнее меню и шторку — иначе раздел прятался бы в одном месте и
 * торчал в другом.
 */
export function useVisibleNavigation() {
  const vpnStore = useVpnStore()
  const poolStore = usePoolStore()

  /**
   * IGRA-110 §11 — флаг `vpn.pool.enabled` выключен: раздела нет ни в меню, ни по
   * адресу (guard роута уводит на «Главную»).
   *
   * Спрашиваем ОБА ответа. Подробный `GET /vpn/pool` есть только на «Главной» и
   * на `/share`, поэтому на холодном заходе в «Устройства» или «Платежи» вкладка
   * выключенного раздела оставалась бы на месте; тот же флаг в овервью приходит
   * вместе с кабинетом на любом экране.
   *
   * Условие именно `=== false`: молчание ответа означает «не знаем», и прятать по
   * нему вкладку у того, кому раздел доступен, нельзя.
   */
  const poolDisabled = computed(
    () => vpnStore.overview?.pool?.enabled === false || poolStore.pool?.enabled === false,
  )

  /**
   * «Ключ» — это экран ключа и страницы подключения. Ключа нет и доступ не
   * заводили — раздел вёл бы на страницу, которой нечего показать.
   *
   * HK-IGRA.664 — а вот ОПЛАТИВШЕМУ БЕЗ КЛЮЧА раздел нужен как никому: у него
   * ключ ещё не выпущен (оплаты из кабинета до HK-IGRA.664 его не выпускали), и
   * страница подключения — второе место, где его можно выпустить одной кнопкой.
   * Прежнее правило прятало пункт ровно у него: человек платил, а раздела
   * «Ключ» в меню не находил — с этой жалобы задача и началась.
   *
   * Условие именно `=== null` на ПРОЧИТАННОМ овервью: молчание ответа означает
   * «не знаем», и прятать по нему раздел у оплатившего человека нельзя — он
   * пришёл сюда как раз за ключом.
   */
  const noKey = computed(
    () =>
      !!vpnStore.overview &&
      vpnStore.overview.key === null &&
      vpnStore.overview.access?.status !== 'active',
  )

  const neverSubscribed = computed(
    () => !!vpnStore.overview && vpnStore.overview.access === null,
  )

  const items = computed<NavigationItem[]>(() => {
    let visible = noKey.value
      ? primaryNavigation.filter((item) => item.name !== 'connect')
      : primaryNavigation

    if (neverSubscribed.value) {
      visible = visible.filter((item) => !HIDDEN_BEFORE_PURCHASE.includes(item.name))
    }

    if (vpnStore.overview?.pool?.role === 'member') {
      return visible.filter((item) => !HIDDEN_FOR_POOL_MEMBER.includes(item.name))
    }

    if (poolDisabled.value) {
      return visible.filter((item) => item.name !== 'pool')
    }

    return visible
  })

  return { items }
}
